import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import AuthContext from "../../hooks/authContext";
import useOnboardingProgress from "../../hooks/useOnboardingProgress";
import {
  consumeOnboardingWidgetResetOnLogin,
  readOnboardingFloatingDismissed,
  readOnboardingModalDismissed,
  readOnboardingSidebarDismissed,
  writeOnboardingFloatingDismissed,
  writeOnboardingModalDismissed,
  writeOnboardingSidebarDismissed,
} from "../../utils/onboardingWidgetDismiss";
import {
  getCompanySizeValue,
  isCompanySizeReady,
  isLargeCompanyByEmployeeCount,
  shouldShowOnboardingCallOption,
} from "../../utils/companyEmployeeCount";

const DEFAULT_ONBOARDING_EXPERIENCE = {
  loading: false,
  progress: {
    profileComplete: false,
    clientAdded: false,
    buildingAdded: false,
    crewAdded: false,
    shiftScheduled: false,
  },
  completedSteps: 0,
  totalSteps: 4,
  modalCompletedSteps: 0,
  modalTotalSteps: 4,
  modalProgressPercentage: 0,
  isComplete: false,
  hasShiftsBooked: false,
  isCompanyUser: false,
  userData: null,
  optionalProfileFieldsMissing: {
    companyAddress: false,
    companyZip: false,
  },
  optionalProfileFieldLabels: [],
  hasOptionalProfileFieldsMissing: false,
  isModalOpen: false,
  isDecisionModalOpen: false,
  showOnboardingCallCta: false,
  usesLargeCompanyOnboarding: false,
  openModal: () => {},
  openChecklistModal: () => {},
  closeModal: () => {},
  minimizeModal: () => {},
  minimizeDecisionModal: () => {},
  dismissForSession: () => {},
  dismissFloatingWidget: () => {},
  dismissSidebarIndicator: () => {},
  isFloatingWidgetDismissed: false,
  isSidebarIndicatorDismissed: false,
  isExperienceReady: false,
  allowCompactOnboarding: false,
  shouldShowIndicator: false,
  showCompletionIndicator: false,
};

const OnboardingExperienceContext = createContext(DEFAULT_ONBOARDING_EXPERIENCE);

export const OnboardingExperienceProvider = ({ children, concurrentSessionModalOpen = false }) => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const isCompanyUser = auth?.userProfile?.role === "companyUser";
  const userId = isCompanyUser ? auth?.userProfile?.id : null;
  const onboarding = useOnboardingProgress(userId);
  const sizeProfile = onboarding.userData || auth?.userProfile;
  const isCompanySizeResolved = isCompanySizeReady(sizeProfile, {
    loading: onboarding.loading,
    hasUserData: Boolean(onboarding.userData),
  });
  const usesLargeCompanyOnboarding = isLargeCompanyByEmployeeCount(
    getCompanySizeValue(sizeProfile)
  );
  const showOnboardingCallCta = shouldShowOnboardingCallOption(sizeProfile);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [showCompletionIndicator, setShowCompletionIndicator] = useState(false);
  const [isFloatingWidgetDismissed, setIsFloatingWidgetDismissed] = useState(false);
  const [isSidebarIndicatorDismissed, setIsSidebarIndicatorDismissed] = useState(false);
  const [allowCompactOnboarding, setAllowCompactOnboarding] = useState(false);
  const [isExperienceReady, setIsExperienceReady] = useState(false);
  const initialCompletionRef = useRef(null);
  const initializedUserIdRef = useRef(null);
  const manualModalDismissRef = useRef(false);
  const openedChecklistIntentionallyRef = useRef(false);
  const hasAutoOpenedRef = useRef(false);
  const usesLargeCompanyOnboardingRef = useRef(usesLargeCompanyOnboarding);

  usesLargeCompanyOnboardingRef.current = usesLargeCompanyOnboarding;

  useEffect(() => {
    if (!userId) {
      initializedUserIdRef.current = null;
      manualModalDismissRef.current = false;
      hasAutoOpenedRef.current = false;
      setIsExperienceReady(false);
      setIsFloatingWidgetDismissed(false);
      setIsSidebarIndicatorDismissed(false);
      setAllowCompactOnboarding(false);
      setIsModalOpen(false);
      setIsDecisionModalOpen(false);
      setShowCompletionIndicator(false);
      initialCompletionRef.current = null;
      return;
    }

    if (initializedUserIdRef.current === userId) {
      return;
    }

    initializedUserIdRef.current = userId;
    manualModalDismissRef.current = false;
    openedChecklistIntentionallyRef.current = false;
    hasAutoOpenedRef.current = false;
    setIsExperienceReady(false);

    if (consumeOnboardingWidgetResetOnLogin(userId)) {
      setIsFloatingWidgetDismissed(false);
      setIsSidebarIndicatorDismissed(false);
      setAllowCompactOnboarding(false);
    } else {
      setIsFloatingWidgetDismissed(readOnboardingFloatingDismissed(userId));
      setIsSidebarIndicatorDismissed(readOnboardingSidebarDismissed(userId));
      setAllowCompactOnboarding(readOnboardingModalDismissed(userId));
    }

    setIsModalOpen(false);
    setIsDecisionModalOpen(false);
    setShowCompletionIndicator(false);
    initialCompletionRef.current = null;
  }, [userId]);

  const revealCompactOnboarding = useCallback(() => {
    manualModalDismissRef.current = true;
    setIsModalOpen(false);
    setIsDecisionModalOpen(false);
    setIsFloatingWidgetDismissed(false);
    setAllowCompactOnboarding(true);
  }, []);

  const dismissForSession = useCallback(() => {
    writeOnboardingModalDismissed(userId);
    revealCompactOnboarding();
  }, [revealCompactOnboarding, userId]);

  const minimizeModal = useCallback(() => {
    revealCompactOnboarding();
  }, [revealCompactOnboarding]);

  const dismissFloatingWidget = useCallback(() => {
    writeOnboardingFloatingDismissed(userId);
    setIsFloatingWidgetDismissed(true);
  }, [userId]);

  const dismissSidebarIndicator = useCallback(() => {
    writeOnboardingSidebarDismissed(userId);
    setIsSidebarIndicatorDismissed(true);
  }, [userId]);

  const openModal = useCallback(() => {
    openedChecklistIntentionallyRef.current = false;
    setIsModalOpen(false);
    if (usesLargeCompanyOnboardingRef.current) {
      setIsDecisionModalOpen(true);
      return;
    }
    setIsDecisionModalOpen(false);
    setIsModalOpen(true);
  }, []);

  const openChecklistModal = useCallback(() => {
    openedChecklistIntentionallyRef.current = true;
    setIsDecisionModalOpen(false);
    setIsModalOpen(true);
  }, []);

  const minimizeDecisionModal = useCallback(() => {
    revealCompactOnboarding();
  }, [revealCompactOnboarding]);

  const closeModal = useCallback(() => {
    minimizeModal();
  }, [minimizeModal]);

  const openOnboardingModal = useCallback(() => {
    if (usesLargeCompanyOnboardingRef.current) {
      setIsDecisionModalOpen(true);
      setIsModalOpen(false);
      return;
    }

    setIsDecisionModalOpen(false);
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    if (
      !isExperienceReady ||
      !usesLargeCompanyOnboarding ||
      !isModalOpen ||
      openedChecklistIntentionallyRef.current ||
      allowCompactOnboarding
    ) {
      return;
    }
    setIsModalOpen(false);
    setIsDecisionModalOpen(true);
  }, [allowCompactOnboarding, isExperienceReady, isModalOpen, usesLargeCompanyOnboarding]);

  useEffect(() => {
    if (!userId || !isCompanyUser) {
      return;
    }

    if (onboarding.loading || !isCompanySizeResolved) {
      return;
    }

    setIsExperienceReady(true);
  }, [userId, isCompanyUser, onboarding.loading, isCompanySizeResolved]);

  useEffect(() => {
    if (!concurrentSessionModalOpen) {
      return;
    }
    setIsModalOpen(false);
    setIsDecisionModalOpen(false);
  }, [concurrentSessionModalOpen]);

  useEffect(() => {
    if (!userId || !isCompanyUser || !isExperienceReady) {
      return;
    }

    if (initialCompletionRef.current === true && !onboarding.isComplete) {
      initialCompletionRef.current = false;
      setShowCompletionIndicator(false);
    }

    if (initialCompletionRef.current === null) {
      initialCompletionRef.current = onboarding.isComplete;

      if (onboarding.isComplete) {
        setShowCompletionIndicator(false);
        return;
      }

      setShowCompletionIndicator(false);
    }

    const isDashboardRoute = location?.pathname === "/" || location?.pathname === "/dashboard";
    const modalDismissedForSession = readOnboardingModalDismissed(userId);
    const shouldAutoOpen =
      !hasAutoOpenedRef.current &&
      !onboarding.isComplete &&
      !modalDismissedForSession &&
      !manualModalDismissRef.current &&
      !isModalOpen &&
      !isDecisionModalOpen &&
      !allowCompactOnboarding &&
      !concurrentSessionModalOpen &&
      isDashboardRoute;

    if (shouldAutoOpen) {
      hasAutoOpenedRef.current = true;
      openOnboardingModal();
      return;
    }

    if (!initialCompletionRef.current && onboarding.isComplete) {
      setIsModalOpen(false);
      setIsDecisionModalOpen(false);
      setAllowCompactOnboarding(true);
      setShowCompletionIndicator(true);
      return;
    }

    if (!onboarding.isComplete) {
      setShowCompletionIndicator(false);
    }
  }, [
    allowCompactOnboarding,
    concurrentSessionModalOpen,
    isDecisionModalOpen,
    isExperienceReady,
    isModalOpen,
    onboarding.isComplete,
    openOnboardingModal,
    userId,
    isCompanyUser,
    location?.pathname,
  ]);

  const shouldShowIndicator = useMemo(() => {
    if (!isExperienceReady || !userId || !onboarding.isCompanyUser) {
      return false;
    }

    return !onboarding.isComplete || showCompletionIndicator;
  }, [
    isExperienceReady,
    onboarding.isCompanyUser,
    onboarding.isComplete,
    showCompletionIndicator,
    userId,
  ]);

  const value = useMemo(
    () => ({
      ...onboarding,
      hasShiftsBooked: onboarding.progress.shiftScheduled,
      isModalOpen: isModalOpen && !concurrentSessionModalOpen,
      isDecisionModalOpen: isDecisionModalOpen && !concurrentSessionModalOpen,
      showOnboardingCallCta,
      usesLargeCompanyOnboarding,
      openModal,
      openChecklistModal,
      closeModal,
      minimizeModal,
      minimizeDecisionModal,
      dismissForSession,
      dismissFloatingWidget,
      dismissSidebarIndicator,
      isFloatingWidgetDismissed,
      isSidebarIndicatorDismissed,
      isExperienceReady,
      allowCompactOnboarding,
      shouldShowIndicator,
      showCompletionIndicator,
    }),
    [
      dismissFloatingWidget,
      dismissSidebarIndicator,
      dismissForSession,
      isFloatingWidgetDismissed,
      isSidebarIndicatorDismissed,
      isExperienceReady,
      allowCompactOnboarding,
      concurrentSessionModalOpen,
      isDecisionModalOpen,
      isModalOpen,
      onboarding,
      minimizeDecisionModal,
      minimizeModal,
      openChecklistModal,
      openModal,
      closeModal,
      showOnboardingCallCta,
      usesLargeCompanyOnboarding,
      shouldShowIndicator,
      showCompletionIndicator,
    ]
  );

  return (
    <OnboardingExperienceContext.Provider value={value}>
      {children}
    </OnboardingExperienceContext.Provider>
  );
};

export const useOnboardingExperience = () => {
  const context = useContext(OnboardingExperienceContext);
  return context;
};
